import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PriceChangeModal = ({ 
  isOpen, 
  onClose, 
  item, 
  onConfirm 
}) => {
  const [newPrice, setNewPrice] = useState(item?.price || '');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});

  if (!isOpen || !item) return null;

  const calculateNewMargin = () => {
    const cost = item?.cost;
    const price = parseFloat(newPrice) || 0;
    if (cost === 0) return 0;
    return ((price - cost) / cost * 100)?.toFixed(2);
  };

  const newMargin = calculateNewMargin();
  const marginColor = newMargin >= 30 ? 'text-success' : newMargin >= 15 ? 'text-warning' : 'text-error';

  const handleSubmit = (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!newPrice || parseFloat(newPrice) <= 0) {
      newErrors.price = 'Ingrese un precio válido';
    }
    if (!reason?.trim()) {
      newErrors.reason = 'El motivo es obligatorio';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onConfirm({
      itemId: item?.id,
      oldPrice: item?.price,
      newPrice: parseFloat(newPrice),
      reason: reason?.trim(),
      timestamp: new Date()?.toISOString()
    });

    setNewPrice('');
    setReason('');
    setErrors({});
  };

  const handleClose = () => {
    setNewPrice(item?.price);
    setReason('');
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg border border-border shadow-elevation-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-warning/10 p-2 rounded-lg">
              <Icon name="DollarSign" size={24} className="text-warning" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                Cambio de Precio
              </h2>
              <p className="text-sm caption text-muted-foreground">
                Requiere justificación obligatoria
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth press-effect focus-ring"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-heading font-semibold text-foreground mb-3">
              Item a Modificar
            </h3>
            <p className="text-base font-body text-foreground">
              {item?.description}
            </p>
            <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-border">
              <div>
                <p className="text-xs caption text-muted-foreground mb-1">Precio Actual</p>
                <p className="text-lg font-heading font-bold text-foreground data-text">
                  ${item?.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                </p>
              </div>
              <div>
                <p className="text-xs caption text-muted-foreground mb-1">Margen Actual</p>
                <p className="text-lg font-heading font-bold text-primary data-text">
                  {item?.margin?.toFixed(2)}%
                </p>
              </div>
            </div>
          </div>

          <Input
            label="Nuevo Precio (MXN)"
            type="number"
            required
            min="0"
            step="0.01"
            value={newPrice}
            onChange={(e) => {
              setNewPrice(e?.target?.value);
              if (errors?.price) setErrors(prev => ({ ...prev, price: '' }));
            }}
            error={errors?.price}
            placeholder="0.00"
            description="Ingrese el nuevo precio unitario"
          />

          {newPrice && (
            <div className="bg-primary/5 rounded-lg border border-primary/20 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm caption text-muted-foreground">Nuevo Margen:</span>
                <span className={`text-xl font-heading font-bold ${marginColor} data-text`}>
                  {newMargin}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm caption text-muted-foreground">Diferencia:</span>
                <span className={`text-base font-body font-semibold data-text ${
                  parseFloat(newPrice) > item?.price ? 'text-success' : 'text-error'
                }`}>
                  {parseFloat(newPrice) > item?.price ? '+' : ''}
                  ${(parseFloat(newPrice) - item?.price)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                </span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Motivo del Cambio <span className="text-error">*</span>
            </label>
            <textarea
              required
              value={reason}
              onChange={(e) => {
                setReason(e?.target?.value);
                if (errors?.reason) setErrors(prev => ({ ...prev, reason: '' }));
              }}
              rows={4}
              className={`
                w-full px-4 py-3 rounded-lg border font-body text-sm
                bg-background text-foreground
                transition-smooth focus-ring
                ${errors?.reason 
                  ? 'border-error focus:border-error' :'border-input focus:border-ring'
                }
              `}
              placeholder="Explique el motivo del cambio de precio (mínimo 10 caracteres)"
            />
            {errors?.reason && (
              <p className="mt-2 text-sm caption text-error flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.reason}
              </p>
            )}
            <p className="mt-2 text-xs caption text-muted-foreground">
              Este cambio quedará registrado en la auditoría del sistema
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              fullWidth
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Check"
              iconPosition="left"
              fullWidth
            >
              Confirmar Cambio
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceChangeModal;